import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Target, X } from 'lucide-react';

interface EditarMetaDoTimeModalProps {
    /** Mês da meta, por extenso ("outubro de 2026"). */
    mesLabel: string;
    valorAtual: number | null;
    onSave: (valor: number, observacao: string) => Promise<void>;
    onClose: () => void;
}

/**
 * Edita a meta do time do Comercial (kWh aprovado no mês) a partir do Analytics.
 * Grava em `metas_mensais` (indicador com_kwh) — a MESMA linha da aba Metas do Painel
 * Executivo, lida também pela Visão Geral. Ver src/lib/metasComerciais.ts.
 */
const EditarMetaDoTimeModal: React.FC<EditarMetaDoTimeModalProps> = ({ mesLabel, valorAtual, onSave, onClose }) => {
    const [valor, setValor] = useState<string>(valorAtual != null && valorAtual > 0 ? String(Math.round(valorAtual)) : '');
    const [observacao, setObservacao] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const numero = Number(valor);
    const valido = valor.trim() !== '' && Number.isFinite(numero) && numero > 0;

    const salvar = async () => {
        if (!valido || salvando) return;
        setSalvando(true);
        setErro(null);
        try {
            await onSave(Math.round(numero), observacao);
        } catch (e: any) {
            setErro(e?.message || 'Não foi possível salvar a meta.');
            setSalvando(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-primary/10 text-primary">
                            <Target size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Meta do Comercial</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">{mesLabel.charAt(0).toUpperCase() + mesLabel.slice(1)}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">kWh aprovado no mês, todos os canais</label>
                        <div className="flex items-center gap-1.5">
                            <input
                                type="number"
                                min={1}
                                value={valor}
                                onChange={e => setValor(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') salvar(); }}
                                autoFocus
                                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-gray-100 text-right text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <span className="text-[11px] text-gray-400">kWh</span>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Observação (opcional)</label>
                        <input
                            value={observacao}
                            onChange={e => setObservacao(e.target.value)}
                            placeholder="Ex.: rampa da equipe nova"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <p className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                        É a mesma meta da aba Metas do Painel Executivo e da Visão Geral: mudar aqui muda lá.
                        Vale deste mês em diante, até alguém definir outra. As metas por pessoa ficam na Folha de Pagamento.
                    </p>
                    {erro && <p className="text-xs text-red-500">{erro}</p>}
                </div>

                <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={salvar}
                        disabled={!valido || salvando}
                        className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                        {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default EditarMetaDoTimeModal;
